import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import './GestdocChat.css';
import API_URL from '../../utils/apiBaseUrl';

const GestdocChat = ({ 
    context = 'flow', 
    contextData: propContextData = {},
    position = 'bottom-right',
    primaryColor = '#2196F3'
}) => {
    // Obtener el estado completo del reducer documento
    // Los datos del API se hacen spread directamente en state.documento
    const documentoState = useSelector((state) => state.documento);
    
    // Construir contextData dinámicamente con información del documento y formulario
    const { contextData, documentName, formName } = React.useMemo(() => {
        // documentoState tiene los datos del API con spread, incluyendo "documento" que tiene info del BPMN
        if (documentoState && documentoState.documento && typeof documentoState.documento === 'object') {
            const docInfo = documentoState.documento; // Info del BPMN/schema
            const participant = docInfo.participants?.[0];
            
            let formSchema = null;
            try {
                formSchema = participant?.form?._stringJson 
                    ? JSON.parse(participant.form._stringJson) 
                    : null;
            } catch (e) {
                console.error('Error parsing form schema:', e);
            }
            
            const formTitle = participant?.form?._title || formSchema?.title || 'Formulario';
            
            // Extraer campos del formulario con información detallada
            const fields = formSchema?.properties 
                ? Object.keys(formSchema.properties).map(key => {
                    const prop = formSchema.properties[key];
                    return {
                        name: key,
                        title: prop.title || key,
                        type: prop.type || 'string',
                        description: prop.description || '',
                        enum: prop.enum || null
                    };
                })
                : [];
            
            return {
                contextData: {
                    ...propContextData,
                    flowName: docInfo._nameSchema,
                    flowId: docInfo._id,
                    formName: formTitle,
                    formId: participant?.form?._id,
                    currentStep: participant?.next?.name || formTitle,
                    fields: fields
                },
                documentName: docInfo._nameSchema,
                formName: formTitle
            };
        }
        return { 
            contextData: propContextData, 
            documentName: null, 
            formName: null 
        };
    }, [documentoState, propContextData]);
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && messages.length === 0) {
            fetchSuggestions();
        }
    }, [isOpen]);

    const fetchSuggestions = async () => {
        try {
            const response = await fetch(`${API_URL}/api/chat/suggestions?context=${context}`);
            const data = await response.json();
            setSuggestions(data.suggestions || []);
        } catch (error) {
            console.error('Error fetching suggestions:', error);
        }
    };

    const sendMessage = async (messageText) => {
        if (!messageText.trim() || isLoading) return;

        const userMessage = { role: 'user', content: messageText };
        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);
        setSuggestions([]);

        try {
            const response = await fetch(`${API_URL}/api/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message: messageText,
                    conversationHistory: messages,
                    context: context,
                    contextData: contextData
                }),
            });

            const data = await response.json();

            if (response.ok) {
                const assistantMessage = { 
                    role: 'assistant', 
                    content: data.message,
                    shouldReferToLawyer: data.shouldReferToLawyer
                };
                setMessages(prev => [...prev, assistantMessage]);
            } else {
                const errorMessage = { 
                    role: 'assistant', 
                    content: data.message || 'Lo siento, ocurrió un error. Intenta de nuevo.',
                    isError: true
                };
                setMessages(prev => [...prev, errorMessage]);
            }
        } catch (error) {
            console.error('Chat error:', error);
            const errorMessage = { 
                role: 'assistant', 
                content: 'No se pudo conectar con el asistente. Verifica tu conexión.',
                isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage(inputValue);
    };

    const handleSuggestionClick = (suggestion) => {
        sendMessage(suggestion);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage(inputValue);
        }
    };

    return (
        <div className={`gestdoc-chat-container ${position}`}>
            {isOpen && (
                <div className="gestdoc-chat-window">
                    <div 
                        className="gestdoc-chat-header"
                        style={{ backgroundColor: primaryColor }}
                    >
                        <div className="gestdoc-chat-header-info">
                            <div className="gestdoc-chat-avatar">
                                <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
                                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                                </svg>
                            </div>
                            <div>
                                <div className="gestdoc-chat-title">Asistente Gestdoc</div>
                                <div className="gestdoc-chat-subtitle">
                                    {isLoading ? 'Escribiendo...' : 'En línea'}
                                </div>
                            </div>
                        </div>
                        <button 
                            className="gestdoc-chat-close"
                            onClick={() => setIsOpen(false)}
                        >
                            <svg viewBox="0 0 24 24" fill="white" width="20" height="20">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                            </svg>
                        </button>
                    </div>

                    {(documentName || formName) && (
                        <div className="gestdoc-chat-context-bar">
                            {documentName && (
                                <div className="gestdoc-chat-context-item document">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                                    </svg>
                                    <span>{documentName}</span>
                                </div>
                            )}
                            {formName && (
                                <div className="gestdoc-chat-context-item form">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                                    </svg>
                                    <span>{formName}</span>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="gestdoc-chat-messages">
                        {messages.length === 0 && (
                            <div className="gestdoc-chat-welcome">
                                <div className="gestdoc-chat-welcome-icon">
                                    <svg viewBox="0 0 24 24" fill={primaryColor} width="48" height="48">
                                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                                    </svg>
                                </div>
                                <h3>¡Hola! Soy tu asistente</h3>
                                <p>{documentName ? '¿Necesitas ayuda con este formulario?' : '¿En qué puedo ayudarte hoy?'}</p>
                            </div>
                        )}

                        {messages.map((msg, index) => (
                            <div 
                                key={index} 
                                className={`gestdoc-chat-message ${msg.role} ${msg.isError ? 'error' : ''}`}
                            >
                                <div className="gestdoc-chat-message-content">
                                    {msg.content.split('\n').map((line, i) => (
                                        <span key={i}>
                                            <span dangerouslySetInnerHTML={{ 
                                                __html: line
                                                    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                                                    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
                                                    .replace(/`([^`]+)`/g, '<code>$1</code>')
                                            }} />
                                            {i < msg.content.split('\n').length - 1 && <br />}
                                        </span>
                                    ))}
                                </div>
                                {msg.shouldReferToLawyer && (
                                    <div className="gestdoc-chat-lawyer-notice">
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                                        </svg>
                                        <span>Para este caso, te recomendamos consultar con un abogado</span>
                                        <div className="gestdoc-chat-lawyer-links">
                                            <a 
                                                href="https://wa.me/56984844444?text=Hola,%20necesito%20asesor%C3%ADa%20legal" 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="gestdoc-chat-lawyer-btn whatsapp"
                                            >
                                                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                                </svg>
                                                WhatsApp
                                            </a>
                                            <a 
                                                href="https://gestdoc.cl/contacto/" 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="gestdoc-chat-lawyer-btn form"
                                            >
                                                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                                                </svg>
                                                Formulario
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}

                        {isLoading && (
                            <div className="gestdoc-chat-message assistant">
                                <div className="gestdoc-chat-typing">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {suggestions.length > 0 && messages.length === 0 && (
                        <div className="gestdoc-chat-suggestions">
                            {suggestions.map((suggestion, index) => (
                                <button
                                    key={index}
                                    className="gestdoc-chat-suggestion"
                                    onClick={() => handleSuggestionClick(suggestion)}
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>
                    )}

                    <form className="gestdoc-chat-input-area" onSubmit={handleSubmit}>
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Escribe tu mensaje..."
                            disabled={isLoading}
                        />
                        <button 
                            type="submit" 
                            disabled={!inputValue.trim() || isLoading}
                            style={{ backgroundColor: primaryColor }}
                        >
                            <svg viewBox="0 0 24 24" fill="white" width="20" height="20">
                                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                            </svg>
                        </button>
                    </form>
                </div>
            )}

            <button 
                className={`gestdoc-chat-fab ${isOpen ? 'hidden' : ''}`}
                onClick={() => setIsOpen(true)}
                style={{ backgroundColor: primaryColor }}
            >
                <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                </svg>
            </button>
        </div>
    );
};

export default GestdocChat;
